import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
  type PercentCrop,
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { getCroppedImage } from './cropImage';
import { useEditorHistory } from './useEditorHistory';
import {
  type AdjustmentState,
  type CropState,
  type EditorState,
  type EditorTab,
  type FilterPreset,
  DEFAULT_ADJUSTMENTS,
  DEFAULT_CROP,
  FILTER_PRESETS,
  buildCSSFilter,
  mergeAdjustments,
} from './imageEditorTypes';
import { ImageEditorHeader } from './ImageEditorHeader';
import { ImageEditorToolbar } from './ImageEditorToolbar';
import { CropControls } from './CropControls';
import { AdjustControls } from './AdjustControls';
import { FilterPresets } from './FilterPresets';
import styles from './ImageEditor.module.css';

interface ImageEditorProps {
  imageSrc: string;
  onSave: (blob: Blob) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const INITIAL_STATE: EditorState = {
  adjustments: { ...DEFAULT_ADJUSTMENTS },
  crop: { ...DEFAULT_CROP },
  activeFilter: 'original',
};

export function ImageEditor({
  imageSrc,
  onSave,
  onCancel,
  isSaving = false,
}: ImageEditorProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>('crop');
  const [comparing, setComparing] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // History-managed editor state (discrete changes only)
  const {
    state: editor,
    push,
    undo,
    redo,
    canUndo,
    canRedo,
    startDrag,
    endDrag,
  } = useEditorHistory<EditorState>(INITIAL_STATE);

  // Live crop — updated every frame by ReactCrop drag, not tracked in history
  const [liveCrop, setLiveCrop] = useState<Crop | undefined>(undefined);

  // Sync live crop when history state changes (undo/redo/reset/aspect change)
  useEffect(() => {
    setLiveCrop(editor.crop.percentCrop);
  }, [editor.crop.percentCrop]);

  // --- Initialize crop when image loads ---
  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
      // Start with a centered 90%-width crop (free aspect)
      const initial: PercentCrop = centerCrop(
        { unit: '%', width: 90, height: (90 * (w / h)) > 90 ? 90 : 90 / (w / h), x: 0, y: 0 },
        w,
        h
      );
      setLiveCrop(initial);
      push({ ...INITIAL_STATE, crop: { ...DEFAULT_CROP, percentCrop: initial } });
    },
    [push]
  );

  // --- Derived CSS filter string ---
  const activePreset = FILTER_PRESETS.find((p) => p.key === editor.activeFilter);
  const mergedAdjustments = useMemo(
    () => mergeAdjustments(activePreset?.adjustments ?? {}, editor.adjustments),
    [activePreset, editor.adjustments]
  );
  const cssFilter = useMemo(() => buildCSSFilter(mergedAdjustments), [mergedAdjustments]);

  // --- Handlers ---
  function updateCrop(partial: Partial<CropState>) {
    const newCropState = { ...editor.crop, ...partial };

    // When aspect changes, re-apply it to the current crop
    if ('aspect' in partial && liveCrop && imgRef.current) {
      const { naturalWidth: w, naturalHeight: h } = imgRef.current;
      let newPercentCrop: PercentCrop;
      if (partial.aspect != null) {
        // Fixed aspect: constrain the current crop
        newPercentCrop = centerCrop(
          makeAspectCrop(
            { unit: '%', width: liveCrop.width ?? 90, x: 0, y: 0, height: 0 },
            partial.aspect,
            w,
            h
          ),
          w,
          h
        );
      } else {
        // Free: keep current crop as-is (just remove constraint)
        newPercentCrop = liveCrop.unit === '%'
          ? liveCrop as PercentCrop
          : { unit: '%', x: liveCrop.x, y: liveCrop.y, width: liveCrop.width, height: liveCrop.height };
      }
      newCropState.percentCrop = newPercentCrop;
      setLiveCrop(newPercentCrop);
    }

    push({ ...editor, crop: newCropState });
  }

  function updateAdjustments(adj: AdjustmentState) {
    push({ ...editor, adjustments: adj });
  }

  function handleFilterSelect(preset: FilterPreset) {
    push({ ...editor, activeFilter: preset.key });
  }

  function handleResetCrop() {
    if (!imgRef.current) return;
    const { naturalWidth: w, naturalHeight: h } = imgRef.current;
    const initial: PercentCrop = centerCrop(
      { unit: '%', width: 90, height: 90, x: 0, y: 0 },
      w,
      h
    );
    setLiveCrop(initial);
    push({ ...editor, crop: { ...DEFAULT_CROP, percentCrop: initial } });
  }

  function handleResetAdjustments() {
    push({ ...editor, adjustments: { ...DEFAULT_ADJUSTMENTS } });
  }

  async function handleSave() {
    const pc = (liveCrop?.unit === '%' ? liveCrop : editor.crop.percentCrop) as PercentCrop | undefined;
    if (!pc || !pc.width || !pc.height) return;
    const blob = await getCroppedImage(
      imageSrc,
      pc,
      editor.crop.rotation,
      editor.crop.flipH,
      editor.crop.flipV,
      cssFilter
    );
    onSave(blob);
  }

  function handleDragStart() {
    startDrag();
  }

  function handleDragEnd() {
    endDrag();
  }

  // --- ReactCrop callbacks ---
  function handleCropChange(_pixelCrop: Crop, percentCrop: PercentCrop) {
    setLiveCrop(percentCrop);
  }

  function handleCropComplete(_pixelCrop: Crop, percentCrop: PercentCrop) {
    push({ ...editor, crop: { ...editor.crop, percentCrop } });
  }

  // --- Keyboard shortcuts ---
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        e.preventDefault(); undo(); return;
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'Z' || e.key === 'z')) {
        e.preventDefault(); redo(); return;
      }
      if (e.key === 'Escape') {
        e.preventDefault(); onCancel(); return;
      }
      if (e.key === '[') {
        e.preventDefault();
        updateCrop({ rotation: (editor.crop.rotation - 90 + 360) % 360 });
        return;
      }
      if (e.key === ']') {
        e.preventDefault();
        updateCrop({ rotation: (editor.crop.rotation + 90) % 360 });
        return;
      }
      if (e.key === '1' && !e.ctrlKey && !e.metaKey) { setActiveTab('crop'); return; }
      if (e.key === '2' && !e.ctrlKey && !e.metaKey) { setActiveTab('adjust'); return; }
      if (e.key === '3' && !e.ctrlKey && !e.metaKey) { setActiveTab('filter'); return; }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor.crop.rotation, undo, redo, onCancel]);

  // --- Image transform style (zoom, rotation, flip) ---
  const imgTransform = useMemo(() => {
    const parts: string[] = [];
    if (editor.crop.zoom !== 1) parts.push(`scale(${editor.crop.zoom})`);
    if (editor.crop.rotation !== 0) parts.push(`rotate(${editor.crop.rotation}deg)`);
    if (editor.crop.flipH) parts.push('scaleX(-1)');
    if (editor.crop.flipV) parts.push('scaleY(-1)');
    return parts.length > 0 ? parts.join(' ') : undefined;
  }, [editor.crop.zoom, editor.crop.rotation, editor.crop.flipH, editor.crop.flipV]);

  const imgFilter = comparing ? undefined : (cssFilter !== 'none' ? cssFilter : undefined);

  return (
    <div className={styles.overlay}>
      <ImageEditorHeader
        canUndo={canUndo}
        canRedo={canRedo}
        isSaving={isSaving}
        onUndo={undo}
        onRedo={redo}
        onCompareDown={() => setComparing(true)}
        onCompareUp={() => setComparing(false)}
        onCancel={onCancel}
        onApply={handleSave}
      />

      <div className={styles.body}>
        <ImageEditorToolbar activeTab={activeTab} onTabChange={setActiveTab}>
          {activeTab === 'crop' && (
            <CropControls
              cropState={editor.crop}
              onUpdate={updateCrop}
              onReset={handleResetCrop}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
          )}
          {activeTab === 'adjust' && (
            <AdjustControls
              adjustments={editor.adjustments}
              onChange={updateAdjustments}
              onResetAll={handleResetAdjustments}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            />
          )}
          {activeTab === 'filter' && (
            <FilterPresets
              imageSrc={imageSrc}
              activeFilter={editor.activeFilter}
              onSelect={handleFilterSelect}
            />
          )}
        </ImageEditorToolbar>

        <div className={styles.canvasArea}>
          <ReactCrop
            crop={liveCrop}
            onChange={handleCropChange}
            onComplete={handleCropComplete}
            aspect={editor.crop.aspect}
            keepSelection
            ruleOfThirds
            className={styles.reactCrop}
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt=""
              onLoad={onImageLoad}
              crossOrigin="anonymous"
              className={styles.cropImage}
              style={{
                transform: imgTransform,
                filter: imgFilter,
              }}
            />
          </ReactCrop>
        </div>
      </div>
    </div>
  );
}
